<template>
  <div>
    <b-row>
      <b-col cols="8">
        <b-row v-if="systemInfo">
          <b-col>
            <div class="sub-title">CPU ({{systemInfo.cpu_count}} cores)</div>
            <vue-chartist :class="cpuTimesChartClass" :data="cpuTimesData"
              :options="gaugeChartOptions" type="Pie"/>
          </b-col>

          <b-col>
            <div class="sub-title">
              {{$t('message.system_memory') | title }}
              <svg v-if="systemInfo.virtual_memory_extra_info" class='icon icon-info' id="memory-extra-info"/>
              <b-tooltip target="memory-extra-info" triggers="hover">
                {{systemInfo.virtual_memory_extra_info}}
              </b-tooltip>
            </div>
            <vue-chartist :class="memChartClass" :data="memData" :options="gaugeChartOptions" type="Pie"/>
          </b-col>

          <b-col>
            <div class="sub-title">
              {{$t('message.system_disk') | title }}
              <svg v-if="systemInfo.virtual_memory_extra_info" class='icon icon-info' id="disk-extra-info"/>
              <b-tooltip target="disk-extra-info" triggers="hover">
                {{systemInfo.disk_usage_extra_info}}
              </b-tooltip>
            </div>
              <vue-chartist :class="diskChartClass" :data="diskData" :options="gaugeChartOptions" type="Pie"/>
          </b-col>
        </b-row>

        <hr id="main-divider"/>

        <b-row id="second-main-row" v-if="systemStatus">
          <b-col cols="6">
            <div class="sub-title">{{$t('message.passman_status') | title}}</div>

            <b-row>
              <b-col v-if="systemStatus.init_config.mode ==='cluster'" cols="5">
                {{$t('message.database_running_local') | title}}
              </b-col>
              <b-col v-else cols="5">
                {{$t('message.database_running') | title}}
              </b-col>
              <b-col cols="5">
                <span :class="{ 'status-ok': systemStatus.database_running, 'status-nok': !systemStatus.database_running }">
                  {{$t('message.status_' + systemStatus.database_running) | title}}
                </span>
              </b-col>
            </b-row>

            <b-row>
              <b-col v-if="systemStatus.init_config.mode ==='cluster'" cols="5">
                {{$t('message.passman_running_local') | title}}
              </b-col>
              <b-col v-else cols="5">
                {{$t('message.passman_running') | title}}
              </b-col>
              <b-col cols="5">
                <span :class="{ 'status-ok': systemStatus.passman_running, 'status-nok': !systemStatus.passman_running }">
                  {{$t('message.status_' + systemStatus.passman_running) | title}}
                </span>
              </b-col>
              <b-col v-if="systemStatus.config_links_enabled">
                <svg v-if="systemStatus.passman_running" class='icon icon-stop' id='passman-stop'
                  @click="redirect('/general_script/services/passman_stop')"/>
                <svg v-else class='icon icon-restart' id='passman-restart'
                  @click="redirect('/general_script/services/passman_restart')"/>
                <b-tooltip target="passman-stop" triggers="hover">
                  {{$t('message.script_services_passman_stop')}}
                </b-tooltip>
                <b-tooltip target="passman-restart" triggers="hover">
                  {{$t('message.script_services_passman_restart')}}
                </b-tooltip>
              </b-col>
            </b-row>

            <b-row v-if="systemStatus.passman_running && systemStatus.agung_runtime_errors">
              <b-col cols="5">{{$t('message.agung_runtime_errors') | title}}</b-col>
              <b-col cols="5">
                <span v-if="Object.keys(systemStatus.agung_runtime_errors).length == 0" class="status-ok">
                    {{$t('message.agung_runtime_errors_none') | title}}
                </span>
                <span v-for="(errData, errName) in systemStatus.agung_runtime_errors">
                  <span class="status-nok" :id="'runtime-error-' + errName">
                    {{errName}}
                  </span>
                  <b-tooltip :target="'runtime-error-' + errName" triggers="hover">
                    <div>{{$t('message.agung_runtime_errors_count')}}: {{errData.count}}</div>
                    <div>{{$t('message.agung_runtime_errors_first_occ_datetime')}}: {{errData.first_occ_datetime}}</div>
                  </b-tooltip>
                </span>
              </b-col>
            </b-row>

            <b-row v-if="systemStatus.passman_running && agungStatus">
              <b-col cols="5">{{$t('message.passman_accessible') | title}}</b-col>
              <b-col cols="5" v-if="agungStatus">
                <span :class="{ 'status-ok': agungStatus.passman_accessible, 'status-nok': !agungStatus.passman_accessible }">
                  {{$t('message.status_' + agungStatus.passman_accessible) | title}}
                </span>
              </b-col>
            </b-row>

            <b-row  v-if="systemStatus && agungStatus">
              <b-col cols="5">{{$t('message.vault_open') | title}}</b-col>
                <b-col cols="7" v-if="systemStatus.init_config.mode === 'cluster'">
                  <span v-for="(node, nodeIndex) in systemStatus.cluster_nodes" :id='"node-vault-"+nodeIndex'
                  :class="{ 'status-ok': node.vault_open, 'status-nok': !node.vault_open }" class="node-vault"
                  @click="openPassmanTabOnNode(node.name)">
                    {{$t('message.status_' + node.vault_open) | title}}
                    <b-tooltip :target='"node-vault-"+nodeIndex' triggers="hover">
                      {{node.name}}
                    </b-tooltip>
                  </span>
                </b-col>
                <b-col cols="5">
                  <span :class="{ 'status-ok': agungStatus.vault_open, 'status-nok': !agungStatus.vault_open }">
                    {{$t('message.status_' + agungStatus.vault_open) | title}}
                  </span>
                </b-col>
                <b-col v-if="systemStatus.init_config.mode !== 'cluster'">
                  <svg v-if="agungStatus && agungStatus.passman_accessible && !agungStatus.vault_open"
                    class='icon icon-restart' @click="openPassmanTabOnNode(systemStatus.ip_address)" id='open-vault'/>
                  <b-tooltip target="open-vault" triggers="hover">
                    {{$t('message.open_vault')}}
                  </b-tooltip>
                </b-col>
            </b-row>

            <b-row>
              <b-col cols="5">{{$t('message.last_open_time') | title}}</b-col>
              <b-col v-if="agungStatus">
                <span v-if="agungStatus.last_vault_open_time">
                  {{agungStatus.last_vault_open_time}}
                </span>
                <span v-else class="status-nok" >
                  {{$t('message.status_null') | title}}
                </span>

              </b-col>
            </b-row>

            <b-row>
              <b-col cols="5">{{$t('message.db_state') | title}}</b-col>
              <b-col cols="5" v-if="agungStatus">
                <span v-if="agungStatus.db_state">
                  {{$t('message.db_state_' + agungStatus.db_state) | title}}
                </span>
                <span v-else class="status-nok" >
                  {{$t('message.status_null') | title}}
                </span>
              </b-col>
            </b-row>

            <b-row>
              <b-col cols="5">{{$t('message.running_task_count') | title}}</b-col>
              <b-col cols="5" v-if="agungStatus">
                <span v-if="agungStatus.running_task_count != undefined && agungStatus.running_task_count != null">
                  {{agungStatus.running_task_count}}
                </span>
                <span v-else class="status-nok" >
                  {{$t('message.status_null') | title}}
                </span>
              </b-col>
            </b-row>
            <b-row>
              <b-col cols="5">{{$t('message.last_login_time') | title}}</b-col>
              <b-col v-if="agungStatus">
                <span v-if="agungStatus.last_login_time">
                  {{agungStatus.last_login_time}}
                </span>
                <span v-else class="status-nok" >
                  {{$t('message.status_null') | title}}
                </span>
              </b-col>
            </b-row>

            <b-row v-for="(users, mins) in agungStatus.active_users" v-if="agungStatus">
              <b-col cols="5">{{$t("message.active_users", {"mins": mins}) | title}}</b-col>
              <b-col cols="1">
                <span :id="'active-users-' + mins">
                  {{users.length ? users.length : 0}}
                </span>
                <b-tooltip :target="'active-users-' + mins" triggers="hover">
                  <div v-for="user in users">
                    {{user.user_upn}}
                  </div>
                </b-tooltip>
              </b-col>
            </b-row>

            <hr/>

            <b-row v-for="(versionVal, versionKey) in versions">
              <b-col cols="5">{{versionKey}}</b-col>
              <b-col>
                <span v-if="versionVal.startsWith('!')" class='comp-needs-update'>
                  {{versionVal.substring(1)}} {{$t('message.comp_needs_update')}}
                </span>
                <span v-else>
                  {{versionVal}}
                </span>
              </b-col>
            </b-row>

            <hr/>

            <b-row v-if="systemStatus.init_config.mode == 'cloud'">
              <b-col cols="5">{{$t('message.instance_state') | title}}</b-col>
              <b-col>{{systemStatus.init_config.instance_state}}</b-col>
            </b-row>

            <b-row>
              <b-col cols="5">{{$t('message.init_state') | title}}</b-col>
              <b-col>{{systemStatus.init_config.state}}</b-col>
            </b-row>

            <hr/>

            <b-row v-if="systemStatus.license_status">
              <b-col cols="10">
                <b-row v-if="systemStatus.license_status == 'valid'">
                  <b-col>
                    {{$t('message.license_company') | title}}
                  </b-col>
                  <b-col>
                    {{systemStatus.license_company}}
                  </b-col>
                </b-row>

                <b-row>
                  <b-col>
                    {{$t('message.license_status') | title}}
                  </b-col>
                  <b-col>
                    <span :class="{ 'status-ok': systemStatus.license_status === 'valid',
                                    'status-nok': systemStatus.license_status != 'valid' }">
                      {{$t('message.license_status_' + systemStatus.license_status) | title}}
                    </span>
                  </b-col>
                </b-row>

                <b-row v-if="systemStatus.license_status == 'valid'">
                  <b-col>
                    {{$t('message.license_expiry') | title}}
                  </b-col>
                  <b-col>
                    {{systemStatus.license_expiry}}
                  </b-col>
                </b-row>

                <b-row v-if="systemStatus.license_status == 'valid'">
                  <b-col>
                    {{$t('message.license_software_update_till') | title}}
                  </b-col>
                  <b-col>
                    {{systemStatus.license_software_update_till}}
                  </b-col>
                </b-row>

                <b-row v-if="systemStatus.license_status == 'valid'">
                  <b-col>
                    {{$t('message.license_user_count') | title}}
                  </b-col>
                  <b-col v-if="systemStatus.allowed_user_count && systemStatus.allowed_user_count > 0">
                    <span id="user_count" v-if="agungStatus">{{agungStatus.user_count}} / </span>
                    <b-tooltip v-if="agungStatus && agungStatus.local_user_count != undefined"
                      target="user_count" triggers="hover">
                      {{$t("message.local")}}: {{agungStatus.local_user_count}}<br>
                      {{$t("message.external")}}: {{agungStatus.user_count - agungStatus.local_user_count}}<br>
                    </b-tooltip>
                    {{systemStatus.allowed_user_count}}
                  </b-col>
                  <b-col v-else>
                    <span id="user_count" v-if="agungStatus">{{agungStatus.user_count}}</span>
                    <b-tooltip v-if="agungStatus && agungStatus.local_user_count != undefined"
                      target="user_count" triggers="hover">
                      {{$t("message.local")}}: {{agungStatus.local_user_count}}<br>
                      {{$t("message.external")}}: {{agungStatus.user_count - agungStatus.local_user_count}}<br>
                    </b-tooltip>
                  </b-col>
                </b-row>

                <b-row v-if="systemStatus.license_status == 'valid'">
                  <b-col>
                    {{$t('message.license_target_count') | title}}
                  </b-col>
                  <b-col v-if="systemStatus.allowed_target_count > 0">
                    <span v-if="agungStatus"> {{agungStatus.target_count}} / </span>
                    {{systemStatus.allowed_target_count}}
                  </b-col>
                  <b-col v-else>
                    <span v-if="agungStatus">{{agungStatus.target_count}}</span>
                  </b-col>
                </b-row>

                <b-row v-if="systemStatus.license_status == 'valid'">
                  <b-col>
                    {{$t('message.license_account_count') | title}}
                  </b-col>
                  <b-col v-if="systemStatus.allowed_account_count > 0">
                    <span v-if="agungStatus">{{agungStatus.account_count}} / </span>
                    {{systemStatus.allowed_account_count}}
                  </b-col>
                  <b-col v-else>
                    <span v-if="agungStatus">{{agungStatus.account_count}}</span>
                  </b-col>
                </b-row>
              </b-col>
              <b-col v-if="systemStatus.config_links_enabled">
                <b-tooltip target="edit-license" triggers="hover">
                  {{$t("message.edit_license") | title}}
                </b-tooltip>
                <svg id="edit-license" class='icon icon-edit' @click="redirect('/general_script/config/license')"/>
              </b-col>
            </b-row>

            <hr/>

            <b-row>
              <b-col cols="10">
                <b-row>
                  <b-col>
                    {{$t('message.script_config_authentication_short') | title}}
                  </b-col>
                  <b-col>
                    <b-row v-for="(provStatus, provName) in systemStatus.auth_sources">
                      <b-col>{{provName}}</b-col>
                      <b-col >
                        <span v-if="provStatusUpdateInProgress == provName">
                          <b-spinner small type="grow"/>
                        </span>
                        <span v-else
                          :class="{ 'status-ok': provStatus == 'ok', 'status-nok': provStatus == 'fail'}" >
                            {{provStatus}}
                        </span>
                        <span v-if="provName != 'local' && !provStatusUpdateInProgress">
                          <b-tooltip :target="'update-prov-status-' + provName" triggers="hover">
                            {{$t("message.update_provider_status") | title}}
                          </b-tooltip>
                          <svg :id="'update-prov-status-' + provName" class='icon icon-restart'
                            @click="updateProvStatus(provName)"/>
                        </span>
                      </b-col>
                    </b-row>
                  </b-col>
                </b-row>
              </b-col>
              <b-col v-if="systemStatus.config_links_enabled">
                <b-tooltip target="edit-providers" triggers="hover">
                  {{$t("message.edit_providers") | title}}
                </b-tooltip>
                <svg id="edit-providers" class='icon icon-edit' @click="redirect('/general_script/config/authentication')"/>
              </b-col>
            </b-row>

            <b-row v-if="systemStatus.vpn_status">
              <b-col cols="10">
                <b-row>
                  <b-col>
                    {{$t('message.vpn_status') | title}}
                  </b-col>
                  <b-col>
                    <span :class="{ 'status-ok': systemStatus.vpn_status === 'connected' }">
                      {{$t('message.vpn_status_' + systemStatus.vpn_status)}}
                    </span>
                  </b-col>
                </b-row>
              </b-col>
              <b-col v-if="systemStatus.config_links_enabled">
                <svg class='icon icon-edit' @click="redirect('/general_script/config/vpn_connection')"/>
              </b-col>
            </b-row>
          </b-col>

          <b-col v-if="systemStatus">
            <div class="sub-title">{{$t('message.system_status') | title}}</div>

            <b-row>
              <b-col cols="10">
                <b-row v-if="systemStatus.ip_address">
                  <b-col>{{$t('message.ip_address') | title}}</b-col>
                  <b-col>{{systemStatus.ip_address}}</b-col>
                </b-row>

                <b-row>
                  <b-col>{{$t('message.hostname') | title}}</b-col>
                  <b-col>{{systemStatus.hostname}}</b-col>
                </b-row>

                <b-row>
                  <b-col>{{$t('message.system_time') | title}}</b-col>
                  <b-col>{{systemInfo.system_time}}</b-col>
                </b-row>

                <b-row>
                  <b-col>{{$t('message.last_boot_time') | title}}</b-col>
                  <b-col>{{systemStatus.boot_time}}</b-col>
                </b-row>
              </b-col>
            </b-row>

            <hr/>

            <b-row>
              <b-col cols="10">
                <b-row>
                  <b-col>{{$t('message.time_timezone') | title}}</b-col>
                  <b-col>{{systemStatus.timezone}}</b-col>
                </b-row>

                <b-row>
                  <b-col>{{$t('message.time_ntp_servers') | title}}</b-col>
                  <b-col>
                    <b-row v-for="ntps in systemStatus.ntp_servers">
                      <b-col>
                        {{ntps}}
                      </b-col>
                    </b-row>
                  </b-col>
                </b-row>
              </b-col>
              <b-col>
                <svg class='icon icon-edit' @click="redirect('/general_script/config/time')"/>
              </b-col>
            </b-row>

            <hr/>

            <b-row>
              <b-col cols="10">
                <b-row>
                  <b-col>{{$t('message.last_db_dump_time') | title}}</b-col>
                  <b-col>
                    <span v-if="systemStatus.last_db_dump_time == ''">
                      {{$t('message.last_db_dump_time_none')}}
                    </span>
                    <span v-else>
                      {{systemStatus.last_db_dump_time}}
                    </span>
                  </b-col>
                </b-row>
              </b-col>
              <b-col>
                <svg class='icon icon-add' @click="redirect('/general_script/data/database_backup')"/>
              </b-col>
            </b-row>

            <hr>
            <b-row>
              <b-col cols="10">
                <b-row>
                  <b-col>{{$t('message.email_expiry_notifications') | title}}</b-col>
                  <b-col>
                    {{$t('message.status_' + systemStatus.email_expiry_notification)}}
                  </b-col>
                </b-row>
              </b-col>
              <b-col>
                <svg class='icon icon-edit' @click="redirect('/general_script/config/auto_jobs')"/>
              </b-col>
            </b-row>

            <b-row v-if="systemStatus.email_status=='configured'">
              <b-col cols="5">{{$t('message.email_status_last_email_sent_time') | title}}</b-col>
              <b-col cols="5" v-if="systemStatus">
                <span v-if="systemStatus.last_email_sent_time == ''">
                  {{$t('message.email_status_last_email_sent_time_none')}}
                </span>
                <span v-else>
                  {{systemStatus.last_email_sent_time}}
                </span>
              </b-col>
            </b-row>

            <b-row v-if="systemStatus.email_status=='configured'">
              <b-col cols="5">{{$t('message.email_status_unsent_email_count') | title}}</b-col>
              <b-col cols="5" v-if="agungStatus">{{agungStatus.unsent_email_count}}</b-col>
            </b-row>

            <b-row v-if="systemStatus.email_status != 'configured'">
              <b-col cols="10">
                <b-row>
                  <b-col>{{$t('message.email_status') | title}}</b-col>
                  <b-col>
                    <span class="status-nok">
                      {{$t('message.email_status_' + systemStatus.email_status)}}
                    </span>
                  </b-col>
                </b-row>
              </b-col>
              <b-col>
                <svg class='icon icon-edit' @click="redirect('/general_script/config/email')"/>
              </b-col>
            </b-row>

            <hr/>

            <b-row>
              <b-col cols="5">{{$t('message.init_mode') | title}}</b-col>
              <b-col cols="5">{{systemStatus.mode}}</b-col>
            </b-row>

            <hr v-if="systemStatus.session_management_support"/>

            <b-row v-if="systemStatus.session_management_support">
              <b-col cols="5">{{$t('message.session_management_status') | title}}</b-col>
              <b-col cols="5">
                <span :class="{ 'status-ok': systemStatus.session_management_status, 'status-nok': !systemStatus.session_management_status }">
                  {{$t('message.status_' + systemStatus.session_management_status) | title}}
                </span>
              </b-col>
              <b-col v-if="systemStatus.config_links_enabled">
                <svg v-if="systemStatus.session_management_running" class='icon icon-stop' @click="redirect('/general_script/services/session_management_stop')"/>
                <svg v-else class='icon icon-restart' @click="redirect('/general_script/services/session_management_restart')"/>
              </b-col>
            </b-row>

            <b-row v-if="systemStatus.license_status == 'valid' && systemStatus.session_management_support">
              <b-col cols="5">
                {{$t('message.license_session_management_active_session_count') | title}}
              </b-col>
              <b-col cols="5">
                {{systemStatus.session_management_allowed_active_session_count}} /
                {{systemStatus.session_management_active_session_count}}
                (rec: {{systemStatus.session_management_active_recording_count}})
              </b-col>
              <b-col v-if="systemStatus.config_links_enabled">
                <b-tooltip target="edit-sm" triggers="hover">
                  {{$t("message.edit_sm") | title}}
                </b-tooltip>
                <svg id="edit-sm" class='icon icon-edit' @click="redirect('/general_script/config/session_management')"/>
              </b-col>
            </b-row>

            <b-row v-if="systemStatus.session_management_support">
              <b-col cols="5">
                {{$t('message.session_management_disk_usage') | title}}
              </b-col>
              <b-col cols="5">
                {{systemStatus.session_management_disk_usage_info}}
                <svg class='icon icon-info' id="sm-disk-usage-extra-info"/>
                <b-tooltip target="sm-disk-usage-extra-info" triggers="hover">
                  {{systemStatus.session_management_disk_usage_extra_info}}
                </b-tooltip>
              </b-col>
              <b-col v-if="systemStatus.config_links_enabled">
                <b-tooltip target="edit-sm-storage" triggers="hover">
                  {{$t("message.edit_sm_storage") | title}}
                </b-tooltip>
                <svg id="edit-sm-storage" class='icon icon-edit' @click="redirect('/general_script/config/extend_volume')"/>
              </b-col>
            </b-row>

            <hr/>

            <b-row v-if="systemStatus.init_config.mode ==='cluster'">
              <b-col>{{$t('message.cluster_status') | title}}</b-col>
              <b-col>
                <span :class="{ 'status-ok': clusterAliveNodeCount == 3,
                                'status-hok': clusterAliveNodeCount == 2,
                                'status-nok': clusterAliveNodeCount <= 1 }">
                  {{$t('message.cluster_status_alive_' + clusterAliveNodeCount) | title}}
                </span>
              </b-col>
            </b-row>

            <b-row v-if="systemStatus.init_config.mode ==='cluster'">
              <b-col>{{$t('message.database_replica_set_status') | title}}</b-col>
              <b-col>
                <span :class="{ 'status-ok': systemStatus.database_replica_set_status == '3 up, 0 down',
                  'status-hok': systemStatus.database_replica_set_status == '2 up, 1 down',
                  'status-nok': systemStatus.database_replica_set_status != '3 up, 0 down' &&
                                systemStatus.database_replica_set_status != '2 up, 1 down'}">
                  {{systemStatus.database_replica_set_status | title}}
                </span>
              </b-col>
            </b-row>

            <b-row v-if="systemStatus.init_config.mode ==='cluster'">
              <b-col>{{$t('message.database_primary') | title}}</b-col>
              <b-col>
                <span :class="{'status-nok': systemStatus.database_primary == 'no primary' }">
                  {{systemStatus.database_primary | title}}
                </span>
                </b-col>
            </b-row>

            <b-row v-if="systemStatus.init_config.mode ==='cluster'">
              <b-col>{{$t('message.init_cluster_nodes') | title}}</b-col>
              <b-col>
                <b-row v-for="node in systemStatus.cluster_nodes">
                  <b-col>
                      {{node.name}}
                      <span :class="{ 'status-ok': node.alive, 'status-nok': !node.alive}">
                        {{$t('message.cluster_node_status_' + node.alive) | title}}
                      </span>
                  </b-col>
                </b-row>
              </b-col>
            </b-row>

            <b-row v-if="systemStatus.init_config.mode ==='cluster'">
              <b-col>{{$t('message.script_config_session_management') | title}}</b-col>
              <b-col>{{systemStatus.init_config.guaca_node}}</b-col>
            </b-row>
          </b-col>
        </b-row>

      </b-col>
      <b-col>
        <b-row id="autorefresh">
          <b-col cols="6" id="last-refresh">
            {{$t('message.last_refresh')}}: {{this.lastRefreshString}}
          </b-col>
          <b-col>
            <b-button v-if="autoRefresh=='on'" class="padmin-button" @click="autoRefresh='off'">
              {{$t('message.autorefresh_is_on')}}
            </b-button>
            <b-button v-if="autoRefresh=='off'" class="padmin-button" @click="autoRefresh='on';loadAll()">
              {{$t('message.autorefresh_is_off')}}
            </b-button>
          </b-col>
        </b-row>

        <div v-if="systemInfo && systemInfo.proc_list.length > 0" id="proc-list">
          <div class="sub-title">{{$t('message.proc') | title}}</div>
          <b-row>
            <b-col cols="5">name (pid)</b-col>
            <b-col cols="2">cpu<br>%</b-col>
            <b-col cols="2">mem<br>%</b-col>
            <b-col cols="3">mem<br>MB</b-col>
          </b-row>
          <b-row class="proc-name" v-for="proc in systemInfo.proc_list" v-bind:key="proc">
            <b-col cols="5">{{proc.name}} ({{proc.pid}})</b-col>
            <b-col cols="2">{{proc.cpu_percent}}</b-col>
            <b-col cols="2">{{proc.memory_percent}}</b-col>
            <b-col cols="3">{{proc.memory_info_mb['rss']}}</b-col>
          </b-row>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import VueChartist from "v-chartist"
import "chartist/dist/chartist.min.css"
import { get, post } from "../http"
import { EventBus } from "../event-bus.js";

export default {
  name: "Dashboard",
  components: {
    "vue-chartist": VueChartist
  },

  data() {
    return {
      systemInfo: null,
      systemStatus: null,
      clusterAliveNodeCount: null,
      agungStatus: null,
      versions: null,
      cpuTimesData: {
        series: []
      },
      memData: {
        series: []
      },
      diskData: {
        series: []
      },
      gaugeChartOptions: {
        donut: true,
        donutWidth: 40,
        startAngle: 270,
        total: 200,
        width: "100%",
        height: "200px",
        labelInterpolationFnc: function(value) {
          return Math.round(value) + ' %';
        }
      },
      cpuTimesChartClass: null,
      memChartClass: null,
      diskChartClass: null,
      keyboardInput: '',
      autoRefresh: 'off',
      destroyed: false,
      lastRefreshDate: null,
      provStatusUpdateInProgress: null,
      provStatusError: null
    }
  },

  watch: {
    systemInfo() {
      if (!this.systemInfo) {
        return
      }

      let orangeLimit = 50;
      let redLimit = 90;

      let idleCpu = this.systemInfo.cpu_times_percent.idle
      let usedCpu = (100.0 - idleCpu).toFixed(1)
      this.$set(this.cpuTimesData, "series", [usedCpu, idleCpu])
      this.cpuTimesChartClass = usedCpu < orangeLimit ? 'donut-chart-green' :
        usedCpu < redLimit ? 'donut-chart-orange' : 'donut-chart-red'

      let usedMem = this.systemInfo.virtual_memory.percent
      let freeMem = (100.0 - usedMem).toFixed(1)
      this.$set(this.memData, "series", [usedMem, freeMem])
      this.memChartClass = usedMem < orangeLimit ? 'donut-chart-green' :
        usedMem < redLimit ? 'donut-chart-orange' : 'donut-chart-red'

      let usedDisk = this.systemInfo.disk_usage.percent
      let freeDisk = (100.0 - usedDisk).toFixed(1)
      this.$set(this.diskData, "series", [usedDisk, freeDisk])
      this.diskChartClass = usedDisk < orangeLimit ? 'donut-chart-green' :
        usedDisk < redLimit ? 'donut-chart-orange' : 'donut-chart-red'
    },

    systemStatus() {
      if (this.systemStatus && this.systemStatus.mode == 'cluster') {
        let aliveNodeCount = 0
        for (let node of this.systemStatus.cluster_nodes) {
          if (node.alive) {
            aliveNodeCount += 1
          }
        }
        this.clusterAliveNodeCount = aliveNodeCount
      }
    }
  },

  created() {
    window.addEventListener('keydown', this.keyDownHandler)
  },

  mounted() {
    this.loadVersions()
    this.loadAll()
  },

  destroyed() {
    this.autoRefresh = 'off'
    this.destroyed = true
    window.removeEventListener('keydown', this.keyDownHandler)
  },

  computed: {
    lastRefreshString () {
      if (!this.lastRefreshDate) return ''
      return this.lastRefreshDate.toLocaleString()
    }
  },

  methods: {
    loadAll() {
      this.loadSystemStatus()
      this.loadAgungStatus()
      this.loadSystemInfo()
    },

    loadVersions() {
      get("/versions").then(
        versionResp => {
          this.versions = versionResp.data['versions']
        },
        error => {
          console.log("get infra version failed", error)
        }
      )
    },

    loadSystemInfo() {
      get("/system_info").then(
        resp => {
          this.systemInfo = resp.data
          this.lastRefreshDate = new Date()
          if (!this.destroyed) {
            setTimeout(() => {
              if (this.autoRefresh == 'on' || this.systemInfo.cpu_times_percent.idle < 10) {
                this.loadSystemInfo()
              }
            }, 3000);
          }
        },
        err => {
          console.log("err", err);
        }
      );
    },

    loadSystemStatus() {
      get("/system_status").then(
        resp => {
          this.systemStatus = resp.data
          this.lastRefreshDate = new Date()
          if (!this.destroyed) {
            setTimeout(() => {
              if (this.autoRefresh == 'on') {
                this.loadSystemStatus()
              }
            }, 20000);
          }
        },
        err => {
          console.log("err", err);
        }
      )
    },

    loadAgungStatus() {
      get('/agung_status').then(
        resp => {
          this.agungStatus = resp.data
          this.lastRefreshDate = new Date()
          if (!this.destroyed) {
            setTimeout(() => {
              if (this.autoRefresh == 'on') {
                this.loadAgungStatus()
              }
            }, 20000);
          }
        },
        err => {
          console.log("err", err);
        }
      )
    },

    redirect(link) {
      this.$router.replace(link);
    },

    openPassmanTabOnNode(nodeIp) {
      window.open('https://' + nodeIp, '_blank').focus()
    },

    updateProvStatus(provName) {
      this.provStatusUpdateInProgress = provName
      this.provStatusError = null
      post('/update_agung_provider_status', {'provider_name': provName}, {'Content-Type': 'application/json'}).then(
        resp => {
          this.provStatusUpdateInProgress = null
          this.systemStatus.auth_sources[provName] = resp.data['status']
          this.provStatusError = resp.data['error']
        },
        err => {
          console.log("err", err)
          this.provStatusUpdateInProgress = null
          this.systemStatus.auth_sources[provName] = '?'
        }
      )
    },

    keyDownHandler(e) {
      this.keyboardInput += e.key
      if (this.keyboardInput == 'debug') {
        this.keyboardInput = ''
        post('/debug_mode').then(
          resp => {
              this.$bvToast.toast(resp.data.new_val == '1' ? this.$t('message.info_debug_mode_enabled')
                : this.$t('message.info_debug_mode_disabled'),
                {autoHideDelay: 5000, variant: 'success', noCloseButton: true, toaster: 'b-toaster-bottom-center'})

              if (resp.data.agung_restart_required) {
                this.$root.$emit("bv::show::modal", "passman-restart-modal-id")
              }
              EventBus.$emit("padmin version changed")
          },
          err => {
            console.log("err", err)
          }
        )
      } else if (this.keyboardInput == 'explain') {
        this.keyboardInput = ''
        post('/mongo_explain').then(
          resp => {
              this.$bvToast.toast(resp.data.new_val == '1' ? this.$t('message.info_mongo_explain_enabled')
                : this.$t('message.info_mongo_explain_disabled'),
                {autoHideDelay: 5000, variant: 'success', noCloseButton: true, toaster: 'b-toaster-bottom-center'})

          },
          err => {
            console.log("err", err)
          }
        )
      }
      else if (this.keyboardInput != 'debug'.substring(0, this.keyboardInput.length) &&
              this.keyboardInput != 'explain'.substring(0, this.keyboardInput.length)){
        this.keyboardInput = ''
      }
    }
  }
}
</script>

<style scoped>
@import "./../styles/dashboardComponent.css";
@import "./../styles/variables.css";
</style>

<!-- does not work from dashboardComponent.css -->
<style>
.donut-chart-green .ct-series-a .ct-slice-donut {
  stroke: var(--donut-chart-green-color) !important;
}

.donut-chart-orange .ct-series-a .ct-slice-donut {
  stroke: var(--donut-chart-orange-color) !important;
}

.donut-chart-red .ct-series-a .ct-slice-donut {
  stroke: var(--donut-chart-red-color) !important;
}

.v-chartist-container .ct-series-b .ct-slice-donut {
  stroke: var(--donut-chart-bg-color) !important;
}

.v-chartist-container .ct-label {
  stroke:var(--donut-chart-bg-color);
}
</style>
